import React, { useEffect, useState } from 'react';
import { superAdminApi } from '../../services/api';

interface Plan {
    id: string;
    name: string;
    slug: string;
    description?: string;
    region: string;
    currency: string;
    pricePerMonth: number;
    maxEmployees: number;
    modules: string[];
    isActive: boolean;
}

const REGIONS = ['IN', 'US', 'CA', 'EU', 'AU', 'SA', 'SG'];
const CURRENCIES: Record<string, string> = {
    IN: 'INR', US: 'USD', CA: 'CAD', EU: 'EUR', AU: 'AUD', SA: 'BRL', SG: 'SGD'
};

export default function PlansPage() {
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRegion, setSelectedRegion] = useState<string>('all');
    const [showModal, setShowModal] = useState(false);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

    useEffect(() => {
        loadPlans();
    }, []);

    async function loadPlans() {
        try {
            const data = await superAdminApi.listPlans();
            setPlans(data.plans || []);
        } catch (error) {
            console.error('Failed to load plans:', error);
        } finally {
            setLoading(false);
        }
    }

    const filteredPlans = selectedRegion === 'all'
        ? plans
        : plans.filter(p => p.region === selectedRegion);

    const groupedPlans = REGIONS.reduce((acc, region) => {
        acc[region] = filteredPlans.filter(p => p.region === region);
        return acc;
    }, {} as Record<string, Plan[]>);

    if (loading) {
        return <div className="text-center py-8">Loading plans...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Pricing Plans</h1>
                <button
                    onClick={() => { setEditingPlan(null); setShowModal(true); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg"
                >
                    + New Plan
                </button>
            </div>

            {/* Region Filter */}
            <div className="bg-gray-800 rounded-lg p-4 mb-6 flex gap-2 flex-wrap">
                <button
                    onClick={() => setSelectedRegion('all')}
                    className={`px-3 py-1 rounded ${selectedRegion === 'all' ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                >
                    All Regions
                </button>
                {REGIONS.map(region => (
                    <button
                        key={region}
                        onClick={() => setSelectedRegion(region)}
                        className={`px-3 py-1 rounded ${selectedRegion === region ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    >
                        {region}
                    </button>
                ))}
            </div>

            {/* Plans by Region */}
            {selectedRegion === 'all' ? (
                REGIONS.map(region => {
                    const regionPlans = groupedPlans[region];
                    if (regionPlans.length === 0) return null;
                    return (
                        <div key={region} className="mb-8">
                            <h2 className="text-lg font-semibold mb-3 text-gray-300">{region} ({CURRENCIES[region]})</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {regionPlans.map(plan => (
                                    <PlanCard
                                        key={plan.id}
                                        plan={plan}
                                        onEdit={() => { setEditingPlan(plan); setShowModal(true); }}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPlans.map(plan => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            onEdit={() => { setEditingPlan(plan); setShowModal(true); }}
                        />
                    ))}
                </div>
            )}

            {showModal && (
                <PlanModal
                    plan={editingPlan}
                    onClose={() => setShowModal(false)}
                    onSave={() => { setShowModal(false); loadPlans(); }}
                />
            )}
        </div>
    );
}

function PlanCard({ plan, onEdit }: { plan: Plan; onEdit: () => void }) {
    return (
        <div className={`bg-gray-800 rounded-lg p-4 border ${plan.isActive ? 'border-gray-700' : 'border-red-800 opacity-60'}`}>
            <div className="flex justify-between items-start mb-3">
                <div>
                    <h3 className="font-semibold">{plan.name}</h3>
                    <p className="text-xs text-gray-400">{plan.slug}</p>
                </div>
                {!plan.isActive && (
                    <span className="text-xs bg-red-600 px-2 py-0.5 rounded">Inactive</span>
                )}
            </div>

            <p className="text-2xl font-bold mb-2">
                {plan.currency} {plan.pricePerMonth}
                <span className="text-sm font-normal text-gray-400">/month</span>
            </p>

            <div className="text-sm text-gray-400 mb-3">
                <p>Up to {plan.maxEmployees} employees</p>
                <p>Modules: {plan.modules.join(', ') || 'None'}</p>
            </div>

            <button
                onClick={onEdit}
                className="w-full px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm"
            >
                Edit
            </button>
        </div>
    );
}

function PlanModal({ plan, onClose, onSave }: { plan: Plan | null; onClose: () => void; onSave: () => void }) {
    const [formData, setFormData] = useState({
        name: plan?.name || '',
        slug: plan?.slug || '',
        description: plan?.description || '',
        region: plan?.region || 'US',
        currency: plan?.currency || 'USD',
        pricePerMonth: plan?.pricePerMonth || 0,
        maxEmployees: plan?.maxEmployees || 10,
        modules: plan?.modules || [],
        isActive: plan?.isActive ?? true,
    });
    const [saving, setSaving] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true);
        try {
            if (plan) {
                await superAdminApi.updatePlan(plan.id, formData);
            } else {
                await superAdminApi.createPlan(formData);
            }
            onSave();
        } catch (error) {
            console.error('Failed to save plan:', error);
            alert('Failed to save plan');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">{plan ? 'Edit Plan' : 'New Plan'}</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Name</label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Slug</label>
                            <input
                                type="text"
                                required
                                disabled={!!plan}
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded disabled:opacity-50"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Region</label>
                            <select
                                value={formData.region}
                                onChange={(e) => setFormData({ ...formData, region: e.target.value, currency: CURRENCIES[e.target.value] })}
                                disabled={!!plan}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded disabled:opacity-50"
                            >
                                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">Price/Month</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.pricePerMonth}
                                onChange={(e) => setFormData({ ...formData, pricePerMonth: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Max Employees</label>
                        <input
                            type="number"
                            value={formData.maxEmployees}
                            onChange={(e) => setFormData({ ...formData, maxEmployees: parseInt(e.target.value) || 10 })}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Modules</label>
                        <div className="flex gap-3">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.modules.includes('bingo')}
                                    onChange={(e) => {
                                        const modules = e.target.checked
                                            ? [...formData.modules, 'bingo']
                                            : formData.modules.filter(m => m !== 'bingo');
                                        setFormData({ ...formData, modules });
                                    }}
                                />
                                Bingo
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.modules.includes('leave_tracker')}
                                    onChange={(e) => {
                                        const modules = e.target.checked
                                            ? [...formData.modules, 'leave_tracker']
                                            : formData.modules.filter(m => m !== 'leave_tracker');
                                        setFormData({ ...formData, modules });
                                    }}
                                />
                                Leave Tracker
                            </label>
                        </div>
                    </div>

                    {plan && (
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                            Active
                        </label>
                    )}

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
